import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Text,
  VStack,
  Box,
  useToast
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { auth, updateProfile } from '../../config/firebase.ts';

interface NicknameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NicknameModal: React.FC<NicknameModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

  // Pré-remplir avec le nom Google si disponible
  React.useEffect(() => {
    if (isOpen && auth.currentUser?.displayName) {
      setNickname(auth.currentUser.displayName);
    }
  }, [isOpen]);

  const handleSkip = () => {
    // Marquer que l'utilisateur a vu la modal même s'il a ignoré
    if (auth.currentUser) {
      localStorage.setItem(`nickname_set_${auth.currentUser.uid}`, 'true');
    }
    onClose();
  };

  const handleSubmit = async () => {
    if (!nickname.trim()) {
      toast({
        title: t('errorEnterPseudo'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (nickname.trim().length < 3) {
      toast({
        title: t('errorNicknameTooShort'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (nickname.trim().length > 20) {
      toast({
        title: t('errorNicknameTooLong'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const user = auth.currentUser;
      if (user) {
        // 1. Mettre à jour dans Firebase
        await updateProfile(user, {
          displayName: nickname.trim()
        });
        await user.reload();
        
        // 2. Synchroniser avec le backend
        try {
          const token = await user.getIdToken(true); // Force refresh du token
          const response = await fetch(`${API_URL}/api/user/update-name`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ displayName: nickname.trim() })
          });
          
          if (!response.ok) {
            console.warn('⚠️ Erreur lors de la synchronisation du pseudo avec le backend');
          } else {
            console.log('✅ Pseudo synchronisé avec le backend');
          }
        } catch (backendError) {
          console.error('❌ Erreur backend:', backendError);
          // On continue même si le backend échoue
        }
        
        // Marquer que l'utilisateur a défini son pseudo
        localStorage.setItem(`nickname_set_${user.uid}`, 'true');
        
        toast({
          title: t('nicknameUpdatedSuccess'),
          description: `${t('welcomeMessage')} ${nickname.trim()} ! 🎉`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        
        onClose();
      }
    } catch (error) {
      console.error('Error updating nickname:', error);
      toast({
        title: t('errorMessageDefault'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={() => {}} 
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size={{ base: 'full', md: 'md' }}
    >
      <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.7)" />
      <ModalContent
        bg="linear-gradient(135deg, rgba(28, 35, 56, 0.98), rgba(20, 25, 45, 0.98))"
        border="2px solid"
        borderColor="rgba(0, 168, 255, 0.3)"
        boxShadow="0 20px 60px rgba(0, 168, 255, 0.3)"
        borderRadius={{ base: 0, md: '20px' }}
      >
        <ModalHeader
          textAlign="center"
          fontSize={{ base: '24px', md: '28px' }}
          fontWeight="800"
          bgGradient="linear(to-r, #00A8FF, #E532FF)"
          bgClip="text"
          pt={{ base: 8, md: 6 }}
        >
          {t('chooseNicknameTitle')}
        </ModalHeader>
        
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <Box textAlign="center" fontSize="60px" animation="bounce 2s ease-in-out infinite">
              🎮
            </Box>
            
            <Text 
              color="gray.400" 
              textAlign="center"
              fontSize={{ base: '14px', md: '15px' }}
              px={2}
            >
              {t('chooseNicknameDesc')}
            </Text>
            
            <Input
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={t('usernamePlaceholder')}
              size="lg"
              variant="filled"
              bg="rgba(20, 25, 45, 0.8)"
              color="white"
              borderColor="rgba(139, 148, 179, 0.3)"
              _hover={{
                borderColor: 'rgba(0, 168, 255, 0.5)',
              }}
              _focus={{
                borderColor: '#00A8FF',
                boxShadow: '0 0 15px rgba(0, 168, 255, 0.4)',
                bg: 'rgba(20, 25, 45, 0.9)',
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              autoFocus
              textAlign="center"
              fontSize={{ base: '16px', md: '18px' }}
              fontWeight="600"
            />
            
            <Text fontSize="12px" color="gray.500" textAlign="center">
              {t('nicknameHelp')}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter flexDirection="column" gap={3} pb={{ base: 8, md: 6 }}>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText={t('saving')}
            size="lg"
            width="full"
            height="55px"
            bgGradient="linear(to-r, #00A8FF, #E532FF)"
            color="white"
            fontWeight="700"
            fontSize="18px"
            _hover={{
              transform: 'translateY(-3px)',
              boxShadow: '0 8px 25px rgba(0, 168, 255, 0.5)',
            }}
            _active={{
              transform: 'translateY(-1px)',
            }}
            transition="all 0.3s ease"
            borderRadius="12px"
          >
            {t('continueButton')} →
          </Button>
          
          {auth.currentUser?.displayName && (
            <Button
              onClick={handleSkip}
              size="md"
              width="full"
              variant="ghost"
              color="gray.400"
              fontWeight="600"
              _hover={{
                color: 'gray.300',
                bg: 'rgba(139, 148, 179, 0.1)',
              }}
            >
              {t('skipNicknameButton')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NicknameModal;
